import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ScrollView, Text, View } from "react-native";
import {
  countFeedbackByStatus,
  type FeedbackFilter,
} from "@heraion/shared";
import type { FeedbackInboxProps } from "./types";
import { useFeedbackInbox } from "./useFeedbackInbox";
import { makeInboxStyles } from "./styles";
import { FeedbackRow } from "./FeedbackRow";
import { Chip } from "../primitives/Chip";
import { LoadMoreRow } from "../LoadMoreRow";
import { resolveTheme } from "../theme";
import { useThemePreferenceContext } from "../ThemeContext";

const FILTERS: readonly FeedbackFilter[] = [
  "open",
  "in_progress",
  "shipped",
  "wontfix",
  "all",
];

// Admin-only feedback inbox, rendered with react-native primitives so
// the SAME component runs on mobile (apps/mobile) and on the DOM via
// react-native-web (apps/web-rnw). The host injects apiFetch + the
// dark/light flag; admin-gating is the host's job (this renders only
// once mounted). Triage rules live in @heraion/shared/feedbackInbox.
export function FeedbackInbox({
  apiFetch,
  dark,
  showTitle = true,
}: FeedbackInboxProps) {
  const { t } = useTranslation();
  // Themed palette (family from ThemeContext) — was the static brand palette,
  // so the inbox filter tabs stayed brand-orange in every theme (Antenore
  // 2026-07-01: "è l'inbox che ha i tabs arancioni").
  const family = useThemePreferenceContext()?.preference.family ?? "brand";
  const theme = resolveTheme(dark, family);
  const p = theme.palette;
  const styles = makeInboxStyles(p);
  const {
    items,
    counts,
    error,
    loading,
    loadingMore,
    hasMore,
    filter,
    setFilter,
    busyId,
    patch,
    loadMore,
  } = useFeedbackInbox(apiFetch);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Authoritative server counts (true per-status totals, stable across
  // filter changes + pages). Fall back to counting the loaded list only
  // until the first response carries `counts` (older API / first paint).
  const displayCounts = counts ?? countFeedbackByStatus(items ?? []);
  const headCount = displayCounts[filter];
  const headLabel =
    filter === "all"
      ? t("feedback.headTotal", { count: headCount })
      : t(`feedback.head_${filter}`, { count: headCount });
  const openHighlight = filter === "open" && headCount > 0;

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        {showTitle && (
          <Text style={styles.title}>{t("feedback.inboxTitle")}</Text>
        )}
        <View style={[styles.countPill, openHighlight && styles.countPillOpen]}>
          <Text
            style={[
              styles.countPillText,
              openHighlight && styles.countPillTextOpen,
            ]}
          >
            {loading && counts === null ? t("feedback.loading") : headLabel}
          </Text>
        </View>
      </View>

      <View style={styles.filterRow}>
        {FILTERS.map((s) => (
          <Chip
            key={s}
            theme={theme}
            hitSlop={{ top: 8, bottom: 8 }}
            label={
              (s === "all"
                ? t("feedback.filterAll")
                : t(`feedback.status_${s}`)) + ` (${displayCounts[s]})`
            }
            selected={s === filter}
            onPress={() => setFilter(s)}
          />
        ))}
      </View>

      {error ? <Text style={styles.errorText}>{error}</Text> : null}

      {loading ? (
        <Text style={styles.stateText}>{t("feedback.loading")}</Text>
      ) : !items || items.length === 0 ? (
        <Text style={styles.stateText}>{t("feedback.empty")}</Text>
      ) : (
        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={styles.listContent}
        >
          {items.map((it) => (
            <FeedbackRow
              key={it.id}
              item={it}
              expanded={expandedId === it.id}
              busy={busyId === it.id}
              onToggle={() =>
                setExpandedId((cur) => (cur === it.id ? null : it.id))
              }
              onPatch={(id, body) => void patch(id, body)}
              styles={styles}
              palette={p}
            />
          ))}
          <LoadMoreRow
            theme={theme}
            hasMore={hasMore}
            loading={loadingMore}
            onPress={() => void loadMore()}
          />
        </ScrollView>
      )}
    </View>
  );
}
